import React from 'react';

export default function TermsAndConditions() {
  return (
    <main className="ht-tc-wrapper">
      {/* Premium Vanilla CSS Stylesheet */}
      <style>{`
        :root {
          --ht-primary: #1A365D;
          --ht-accent: #319795;
          --ht-accent-light: #EBF8FA;
          --ht-text-dark: #2D3748;
          --ht-text-muted: #718096;
          --ht-bg-light: #F7FAFC;
          --ht-success: #2F855A;
          --ht-success-bg: #F0FFF4;
          --ht-danger: #9B2C2C;
          --ht-danger-bg: #FFF5F5;
        }

        .ht-tc-wrapper {
          max-width: 960px;
          margin: 60px auto;
          padding: 0 24px;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          color: var(--ht-text-dark);
          line-height: 1.7;
        }

        /* Header Style */
        .ht-tc-header {
          text-align: center;
          margin-bottom: 50px;
          position: relative;
        }

        .ht-tc-header h1 {
          font-size: 2.8rem;
          color: var(--ht-primary);
          font-weight: 800;
          letter-spacing: -0.5px;
          margin-bottom: 12px;
        }

        .ht-tc-header p {
          font-size: 1.1rem;
          color: var(--ht-text-muted);
        }

        .ht-tc-header::after {
          content: "";
          display: block;
          width: 60px;
          height: 4px;
          background: var(--ht-accent);
          margin: 24px auto 0;
          border-radius: 2px;
        }

        /* Intro Feature Block */
        .ht-tc-intro {
          background: linear-gradient(135deg, var(--ht-accent-light) 0%, #FFFFFF 100%);
          border-left: 5px solid var(--ht-accent);
          padding: 24px 30px;
          border-radius: 0 12px 12px 0;
          margin-bottom: 45px;
          box-shadow: 0 4px 15px rgba(49, 151, 149, 0.05);
        }

        .ht-tc-intro h3 {
          margin: 0 0 10px 0;
          color: var(--ht-primary);
          font-size: 1.25rem;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        /* Core Sections */
        .ht-tc-section {
          background: #FFFFFF;
          border: 1px solid #E2E8F0;
          border-radius: 12px;
          padding: 35px;
          margin-bottom: 30px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .ht-tc-section:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.04);
        }

        .ht-tc-section h2 {
          font-size: 1.6rem;
          color: var(--ht-primary);
          margin-top: 0;
          margin-bottom: 20px;
          font-weight: 700;
        }

        /* Customized List Styling */
        .ht-tc-list {
          padding-left: 0;
          list-style: none;
         }

        .ht-tc-list li {
          position: relative;
          padding-left: 24px;
          margin-bottom: 12px;
        }

        .ht-tc-list li::before {
          content: "✦";
          position: absolute;
          left: 0;
          color: var(--ht-accent);
          font-size: 0.9rem;
        }

        /* High-Impact Cancellation Layout */
        .ht-cancellation-container {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 24px;
          margin-top: 25px;
        }

        .ht-policy-card {
          border-radius: 10px;
          padding: 24px;
          border: 1px solid #E2E8F0;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .ht-policy-card.success {
          background-color: var(--ht-success-bg);
          border-color: #C6F6D5;
        }

        .ht-policy-card.danger {
          background-color: var(--ht-danger-bg);
          border-color: #FED7D7;
        }

        .ht-status-badge {
          display: inline-block;
          align-self: flex-start;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 16px;
        }

        .success .ht-status-badge {
          background-color: #C6F6D5;
          color: var(--ht-success);
        }

        .danger .ht-status-badge {
          background-color: #FED7D7;
          color: var(--ht-danger);
        }

        .ht-policy-card h4 {
          margin: 0 0 10px 0;
          font-size: 1.3rem;
          color: var(--ht-primary);
        }

        .ht-policy-card p {
          margin: 0;
          font-size: 0.95rem;
          color: var(--ht-text-dark);
        }

        /* Responsive Breakpoints */
        @media (max-width: 768px) {
          .ht-tc-header h1 { font-size: 2.2rem; }
          .ht-tc-section { padding: 24px; }
          .ht-tc-wrapper { margin: 30px auto; }
        }
      `}</style>

      {/* Semantic Header */}
      <header className="ht-tc-header">
        <h1>Terms & Conditions</h1>
        <p>Your transparent agreement with Himalayas Travels</p>
        <p><small>Effective Date: June 2026</small></p>
      </header>

      {/* Semantic Summary Block */}
      <article className="ht-tc-intro">
        <h3>🏔️ Adventure Transparency Guarantee</h3>
        <p>
          We believe in straightforward policies. Mountain trekking & Tours  demands safety, flexibility, and trust. Our terms ensure that both your journey and your investment are well-protected before you step into the Himalayas.
        </p>
      </article>

      {/* Section 1: Bookings */}
      <div className="ht-tc-section">
        <h2>1. Bookings & Reservations</h2>
        <p>
          To officially confirm your chosen trek, expedition, or tour, a processing deposit is required at registration.
        </p>
        <ul className="ht-tc-list">
          <li><strong>Initial Booking Deposit:</strong> 10% of the total trip fare must be deposited to immediately block local flights, hotels, and permits.</li>
          <li><strong>Balance Completion:</strong> The rest of the 90% balance is required to pay after landing and transfer to hotel before day 2 itinerary Begains. </li>
        </ul>
      </div>

      {/* Section 2: Transparent Cancellations */}
      <div className="ht-tc-section">
        <h2>2. Simple Cancellation Policy</h2>
        <p>
          We maintain a crystal-clear, zero-confusion policy on trip updates and cancellations to eliminate gray areas:
        </p>

        <div className="ht-cancellation-container">
          {/* Rule A: More than 15 days */}
          <article className="ht-policy-card success">
            <div>
              <span className="ht-status-badge">15+ Days Before Trip</span>
              <h4>100% Full Refund</h4>
            </div>
            <p>Cancel your adventure more than 15 days ahead of your scheduled departure, and we will issue a full refund back to your account.</p>
          </article>

          {/* Rule B: Within 15 days */}
          <article className="ht-policy-card danger">
            <div>
              <span className="ht-status-badge">0 to 14 Days Before Trip</span>
              <h4>No Refund (0%)</h4>
            </div>
            <p>Cancellations made within 14 days or less of departure are entirely non-refundable due to strictly non-recoverable local peak-season bookings.</p>
          </article>
        </div>
      </div>

      {/* Section 3: Safety & Risks */}
      <div className="ht-tc-section">
        <h2>3. Altitude, Fitness & Safety</h2>
        <p>
          High-altitude trekking offers unmatched views, but demands physical accountability:
        </p>
        <ul className="ht-tc-list">
          <li><strong>Physical Preparation:</strong> Travelers are fully responsible for ensuring they match the medical fitness standards required for alpine altitudes.</li>
          <li><strong>Guide Authority:</strong> For your protection, group leaders hold total operational authority. If a guide decides a client must descend due to altitude sickness, this rule must be followed immediately.</li>
        </ul>
      </div>

      {/* Section 4: Insurance */}
      <div className="ht-tc-section">
        <h2>4. Travel Insurance Mandate</h2>
        <p>
          <strong>You cannot trek without insurance.</strong> Every participant must secure an active policy covering medical expenses, extreme high-altitude rescue, and emergency helicopter evacuation options up to 6,000 meters.
        </p>
      </div>

      {/* Section 5: Unforeseen Forces */}
      <div className="ht-tc-section">
        <h2>5. Itinerary Flex & Force Majeure</h2>
        <p>
          Himalayan weather changes rapidly. Flight disruptions to remote mountain airstrips (like Lukla or Jomsom) or landslide blockades are natural risks.
        </p>
        <ul className="ht-tc-list">
          <li><strong>Routes & Shifts:</strong> Himalayas Travels keeps the absolute right to tweak routes or delays to safeguard group well-being.</li>
          <li><strong>Extra Expenses:</strong> Costs stemming from unexpected weather delays (like additional nights at lodges or alternative helicopter transfers) belong to the traveler and can be claimed back from your insurance provider.</li>
        </ul>
      </div>
    </main>
  );
}